/* Compliance Pack — candidate-side upload/share + employer-side download view.
 * Self-contained so the candidate and employer dashboards just render one component each. */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { logger } from './utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ============================================================
// CANDIDATE TAB
// ============================================================
export function CandidateCompliancePack() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shares, setShares] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Upload form state
  const [uploadCategory, setUploadCategory] = useState('identity');
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  // Share form state
  const [shareEmail, setShareEmail] = useState('');
  const [shareNote, setShareNote] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d, s, r] = await Promise.all([
        axios.get(`${API}/vault/compliance-pack/categories`),
        axios.get(`${API}/vault/compliance-pack/documents`),
        axios.get(`${API}/vault/compliance-pack/shares`),
        axios.get(`${API}/vault/compliance-readiness`),
      ]);
      setCategories(c.data);
      setDocs(d.data);
      setShares(s.data);
      setReadiness(r.data);
    } catch (err) {
      logger.error('Compliance pack load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    if (!uploadFile) {
      setError('Please choose a file first.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      form.append('category', uploadCategory);
      if (uploadLabel) form.append('label', uploadLabel);
      await axios.post(`${API}/vault/compliance-pack/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadFile(null);
      setUploadLabel('');
      document.getElementById('cp-upload-file').value = '';
      refresh();
    } catch (err) {
      logger.error('Upload failed', err);
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this document from your compliance pack?')) return;
    try {
      await axios.delete(`${API}/vault/compliance-pack/documents/${id}`);
      refresh();
    } catch (err) {
      logger.error('Delete failed', err);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    setShareMessage('');
    if (!shareEmail) return;
    setSharing(true);
    try {
      await axios.post(`${API}/vault/compliance-pack/share`, {
        employer_email: shareEmail,
        note: shareNote || null,
        expiry_hours: 24 * 30,
      });
      setShareEmail('');
      setShareNote('');
      setShareMessage(`✅ Shared with ${shareEmail}`);
      refresh();
    } catch (err) {
      logger.error('Share failed', err);
      setShareMessage(`❌ ${err.response?.data?.detail || 'Share failed'}`);
    } finally {
      setSharing(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this share? The employer will no longer be able to download these documents.')) return;
    try {
      await axios.delete(`${API}/vault/compliance-pack/shares/${id}`);
      refresh();
    } catch (err) {
      logger.error('Revoke failed', err);
    }
  };

  const groupedByCategory = useMemo(() => {
    const grouped = {};
    for (const d of docs) {
      if (!grouped[d.category]) grouped[d.category] = { label: d.category_label, items: [] };
      grouped[d.category].items.push(d);
    }
    return grouped;
  }, [docs]);

  if (loading) return <div className="text-gray-400 py-12 text-center">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="candidate-compliance-pack">
      <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-900">
        <strong>🛡️ About your Compliance Pack:</strong>
        <p className="mt-1">
          Upload your onboarding documents once, then share them securely with a registered Vault Hire employer
          when needed. They can download the docs straight into their HRIS / system of record.
          You always control who can access them — revoke any time. Files are encrypted at rest and only
          the registered employer you've named can open them.
        </p>
      </div>

      {/* Readiness widget */}
      {readiness && (
        <section className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded p-4" data-testid="cp-readiness-widget">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Compliance Readiness</h2>
              <p className="text-xs text-gray-600">
                A privacy-safe progress signal that recruiters can see when reviewing your application. Only the score is shared — never your documents.
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-emerald-700">{readiness.score_pct}%</div>
              <div className="text-xs text-gray-500">{readiness.categories_filled} of {readiness.categories_total} categories</div>
            </div>
          </div>
          <div className="w-full bg-white rounded-full h-3 overflow-hidden border border-emerald-200">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
              style={{ width: `${readiness.score_pct}%` }}
              data-testid="cp-readiness-bar"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
              readiness.tier === 'Complete' ? 'bg-emerald-100 text-emerald-800' :
              readiness.tier === 'Strong'   ? 'bg-blue-100 text-blue-800' :
              readiness.tier === 'Building' ? 'bg-amber-100 text-amber-800' :
              readiness.tier === 'Started'  ? 'bg-orange-100 text-orange-700' :
                                              'bg-gray-100 text-gray-600'
            }`} data-testid="cp-readiness-tier">{readiness.tier}</span>
            {readiness.next_recommended_category && (
              <button
                onClick={() => { setUploadCategory(readiness.next_recommended_category.id); document.getElementById('cp-upload-file')?.focus(); }}
                className="text-xs text-blue-600 hover:underline"
                data-testid="cp-readiness-next-hint"
              >
                💡 Next: add a <strong>{readiness.next_recommended_category.label}</strong> document →
              </button>
            )}
            {!readiness.next_recommended_category && (
              <span className="text-xs text-emerald-700 font-medium">🎉 You've covered all banking categories</span>
            )}
          </div>
        </section>
      )}

      {/* Upload */}
      <section className="bg-white border rounded p-4" data-testid="cp-upload-section">
        <h2 className="text-lg font-semibold mb-3">Upload a document</h2>
        <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-xs text-gray-600">Category</label>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              className="w-full mt-1 px-2 py-2 border rounded text-sm"
              data-testid="cp-upload-category"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-4">
            <label className="text-xs text-gray-600">Label (optional)</label>
            <input
              type="text"
              value={uploadLabel}
              onChange={(e) => setUploadLabel(e.target.value)}
              placeholder="e.g. Passport - January 2026"
              className="w-full mt-1 px-2 py-2 border rounded text-sm"
              data-testid="cp-upload-label"
            />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-gray-600">File (PDF/PNG/JPEG, max 10 MB)</label>
            <input
              id="cp-upload-file"
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full mt-1 text-sm"
              data-testid="cp-upload-file"
            />
          </div>
          <div className="md:col-span-1">
            <button
              type="submit"
              disabled={uploading || !uploadFile}
              className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded disabled:opacity-50"
              data-testid="cp-upload-submit"
            >
              {uploading ? '…' : 'Upload'}
            </button>
          </div>
        </form>
        {error && <p className="text-red-600 text-sm mt-2" data-testid="cp-upload-error">{error}</p>}
      </section>

      {/* My documents */}
      <section className="bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">My documents</h2>
          <span className="text-xs text-gray-500">{docs.length} / 20 documents</span>
        </div>
        {docs.length === 0 ? (
          <div className="text-sm text-gray-400 py-6 text-center">
            No documents yet. Upload your first one above.
          </div>
        ) : (
          <div className="space-y-3" data-testid="cp-doc-list">
            {Object.entries(groupedByCategory).map(([catId, group]) => (
              <div key={catId}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{group.label}</h3>
                <ul className="mt-1 divide-y border rounded">
                  {group.items.map((d) => (
                    <li
                      key={d.id}
                      className="flex items-center justify-between px-3 py-2"
                      data-testid={`cp-doc-${d.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 truncate">{d.label}</p>
                        <p className="text-xs text-gray-500">{d.filename} · {formatBytes(d.size_bytes)} · uploaded {new Date(d.uploaded_at).toLocaleDateString()}</p>
                      </div>
                      <button
                        onClick={() => handleDelete(d.id)}
                        className="text-xs text-red-600 hover:underline"
                        data-testid={`cp-doc-delete-${d.id}`}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Share */}
      <section className="bg-white border rounded p-4">
        <h2 className="text-lg font-semibold mb-3">Share with an employer</h2>
        <p className="text-xs text-gray-600 mb-3">
          Only employers with a registered Vault Hire account can receive a compliance pack — this ensures every download is traceable.
        </p>
        <form onSubmit={handleShare} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-5">
            <label className="text-xs text-gray-600">Employer email</label>
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              placeholder="recruiter@bank.com"
              required
              className="w-full mt-1 px-2 py-2 border rounded text-sm"
              data-testid="cp-share-email"
            />
          </div>
          <div className="md:col-span-5">
            <label className="text-xs text-gray-600">Note (optional)</label>
            <input
              type="text"
              value={shareNote}
              onChange={(e) => setShareNote(e.target.value)}
              placeholder="For my Risk Manager onboarding"
              className="w-full mt-1 px-2 py-2 border rounded text-sm"
              data-testid="cp-share-note"
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={sharing || docs.length === 0}
              className="w-full px-3 py-2 bg-emerald-600 text-white text-sm rounded disabled:opacity-50"
              data-testid="cp-share-submit"
            >
              {sharing ? '…' : 'Share'}
            </button>
          </div>
        </form>
        {shareMessage && <p className="text-sm mt-2" data-testid="cp-share-message">{shareMessage}</p>}
      </section>

      {/* Outgoing shares */}
      <section className="bg-white border rounded p-4">
        <h2 className="text-lg font-semibold mb-3">Active shares</h2>
        {shares.length === 0 ? (
          <p className="text-sm text-gray-400">You have not shared your compliance pack with anyone yet.</p>
        ) : (
          <ul className="divide-y border rounded" data-testid="cp-shares-list">
            {shares.map((s) => (
              <li key={s.id} className="px-3 py-2" data-testid={`cp-share-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {s.employer_email}
                      {s.revoked && <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">revoked</span>}
                      {!s.revoked && new Date(s.expiry_date) < new Date() && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">expired</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {s.document_count} doc{s.document_count !== 1 ? 's' : ''} · shared {new Date(s.created_at).toLocaleString()} · expires {new Date(s.expiry_date).toLocaleDateString()}
                    </p>
                    {(s.downloads || []).length > 0 && (
                      <p className="text-xs text-emerald-700 mt-1">
                        Downloaded {s.downloads.length} time{s.downloads.length !== 1 ? 's' : ''} · last by {s.downloads[s.downloads.length - 1].user_email}
                      </p>
                    )}
                  </div>
                  {!s.revoked && (
                    <button
                      onClick={() => handleRevoke(s.id)}
                      className="text-xs text-red-600 hover:underline"
                      data-testid={`cp-share-revoke-${s.id}`}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ============================================================
// EMPLOYER VIEW
// ============================================================
export function EmployerCompliancePacks() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/employer/compliance-packs`);
      setShares(data);
    } catch (err) {
      logger.error('Failed to load incoming packs', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (shareId) => {
    setError('');
    try {
      const { data } = await axios.get(`${API}/employer/compliance-packs/${shareId}`);
      setSelectedDetail(data);
    } catch (err) {
      logger.error('Failed to load pack detail', err);
      setError(err.response?.data?.detail || 'Pack unavailable');
      setSelectedDetail(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  const handleDownloadDoc = async (shareId, doc) => {
    try {
      const res = await axios.get(`${API}/employer/compliance-packs/${shareId}/download/${doc.id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Download failed', err);
      setError(err.response?.data?.detail || 'Download failed');
    }
  };

  const handleDownloadZip = async (share) => {
    try {
      const res = await axios.get(`${API}/employer/compliance-packs/${share.id}/download-zip`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      const safe = (share.candidate_name || 'candidate').replace(/[^a-zA-Z0-9_-]/g, '');
      a.download = `compliance_pack_${safe}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Zip download failed', err);
      setError(err.response?.data?.detail || 'Download failed');
    }
  };

  if (loading) return <div className="text-gray-400 py-12 text-center">Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="employer-compliance-packs">
      <div className="bg-white border rounded p-4">
        <h2 className="text-lg font-semibold mb-3">Incoming packs</h2>
        {shares.length === 0 ? (
          <p className="text-sm text-gray-400">No compliance packs received yet. Candidates can share their onboarding documents with you using your registered email address.</p>
        ) : (
          <ul className="divide-y border rounded">
            {shares.map((s) => (
              <li
                key={s.id}
                onClick={() => setSelected(s.id)}
                className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${selected === s.id ? 'bg-blue-50' : ''}`}
                data-testid={`emp-cp-row-${s.id}`}
              >
                <p className="text-sm font-medium">{s.candidate_name}</p>
                <p className="text-xs text-gray-500">{s.candidate_email}</p>
                <p className="text-xs text-gray-400 mt-1">{s.document_count} doc{s.document_count !== 1 ? 's' : ''} · shared {new Date(s.created_at).toLocaleDateString()}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-2 bg-white border rounded p-4">
        {!selected ? (
          <p className="text-sm text-gray-400">Select a pack to view its contents.</p>
        ) : error ? (
          <p className="text-sm text-red-600" data-testid="emp-cp-error">{error}</p>
        ) : !selectedDetail ? (
          <p className="text-sm text-gray-400">Loading pack…</p>
        ) : (
          <div data-testid="emp-cp-detail">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold">{selectedDetail.candidate_name}</h2>
                <p className="text-xs text-gray-500">{selectedDetail.candidate_email}</p>
                {selectedDetail.note && (
                  <p className="text-sm text-gray-700 mt-2 italic">"{selectedDetail.note}"</p>
                )}
              </div>
              <button
                onClick={() => handleDownloadZip(selectedDetail)}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded"
                data-testid="emp-cp-download-zip"
              >
                ⬇️ Download all (.zip)
              </button>
            </div>
            <ul className="divide-y border rounded">
              {(selectedDetail.documents || []).map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between px-3 py-2"
                  data-testid={`emp-cp-doc-${d.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded mr-2">{d.category_label}</span>
                      {d.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{d.filename} · {formatBytes(d.size_bytes)}</p>
                  </div>
                  <button
                    onClick={() => handleDownloadDoc(selectedDetail.id, d)}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    data-testid={`emp-cp-download-${d.id}`}
                  >
                    Download
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-4 text-xs text-gray-400">
              💡 Future: SFTP / API delivery into your HRIS — coming soon. For now, download zip and import manually.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
